import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import qs from "query-string";
import {
  alpha,
  ButtonBase,
  Divider,
  InputBase,
  Paper,
  Tooltip,
  useTheme,
  Zoom,
} from "@mui/material";
import { EGIconButton } from "../../../../MainComponents/Button/IconButton/IconButton";
import { useTranslation } from "react-i18next";
import { SelectionMenu } from "./SelectionMenu";
import { useLocation, useNavigate } from "react-router";
/** Search bar with direct access functionality */
export const SearchBar = ({
  inputSearchValue,
  onChangeSearchValue,
  inputSearchType,
  onChangeSearchType,
  onSearch,
  onClear,
  setShowFilters,
  yearSelected,
  onChangeYear,
  resetPagination,
}) => {
  // Custom hooks
  const [t] = useTranslation("report");
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  // Initial States
  const [isFocused, setIsFocused] = useState(false);
  const [anchorElType, setAnchorElType] = useState(null);
  const [anchorElYear, setAnchorElYear] = useState(null);

  const query = qs.parse(location.search);

  // Ignore 'offset' y 'limit'
  const filteredQuery = Object.entries(query).filter(
    ([key]) => key !== "offset" && key !== "limit"
  );

  const hasFilters = filteredQuery.length > 0;
  // Search engine options
  const searchTypeOptions = [
    {
      value: "folio",
      label: "Folio",
      short: "Fol",
      placeholder: t("ReportsList.search_folio"),
    },
    {
      value: "tracking_code",
      label: t("ReportsList.tracking_code"),
      short: t("ReportsList.TC"),
      placeholder: t("ReportsList.search_tracking_code"),
    },
  ];

  const selectedSearchType = useMemo(
    () => searchTypeOptions.find((opt) => opt.value === inputSearchType),
    [inputSearchType]
  );
  // Options years
  // const optionsYearCreated = Array.from(
  //   { length: dayjs().year() - 2015 + 1 },
  //   (_, index) => {
  //     const year = dayjs().year() - index;
  //     return { value: year, label: year.toString() };
  //   }
  // );
  // En SearchBar.jsx
  const currentYear = dayjs().year();
  const optionsYearCreated = Array.from(
    { length: 5 }, // sólo 5 años atrás
    (_, i) => {
      const year = currentYear - i;
      return { value: year, label: year.toString() };
    }
  );

  return (
    <Paper
      sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: 6,
        height: 37,
        border: `1px solid ${theme.palette.blueGray200.main}`,
        boxShadow: isFocused
          ? `0 0 4px -2px ${alpha(theme.palette.common.black, 0.8)}`
          : `0 0 3px -3px ${alpha(theme.palette.common.black, 0.8)}`,
        overflow: "hidden",
        p: "2px 4px",
        transition: "box-shadow 0.3s ease-in-out",
      }}
    >
      {(isFocused || inputSearchValue.trim() !== "") && (
        <EGIconButton
          iconName="search2"
          iconColorHover="black"
          iconSize="20"
          title={t("searchBar.search")}
          sx={{
            opacity: isFocused || inputSearchValue.trim() !== "" ? 1 : 0.3,
            transition: "opacity 0.5s ease-in-out",
          }}
          onClick={() => onSearch(inputSearchValue)}
        />
      )}
      {/* Input */}
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder={selectedSearchType?.placeholder}
        inputProps={{ "aria-label": "search casemanager" }}
        value={inputSearchValue}
        onChange={(e) => {
          const value = e.target.value;
          onChangeSearchValue(value);
          if (value.trim() === "") {
            onClear();
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSearch(inputSearchValue);
          }
        }}
      />
      {/* Clean botton */}
      {inputSearchValue.trim() !== "" && (
        <EGIconButton
          iconName="close_icon"
          onClick={onClear}
          iconColorHover="black"
          title={t("ReportsList.clean_search")}
        />
      )}
      {/* Search engine button */}
      <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
      <Tooltip
        title={t("ReportsList.search_by")}
        arrow
        placement="top"
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 150 }}
      >
        <ButtonBase
          onClick={(e) => setAnchorElType(e.currentTarget)}
          sx={{
            minWidth: 30,
            minHeight: 30,
            px: 0.5,
            borderRadius: "50%",
            color: theme.palette.secondary.light,
            "&:hover": {
              bgcolor: theme.palette.action.hover,
              color: theme.palette.text.secondary,
            },
          }}
        >
          {selectedSearchType?.short}
        </ButtonBase>
      </Tooltip>
      {/* Menu search engine input*/}
      <SelectionMenu
        anchorEl={anchorElType}
        open={Boolean(anchorElType)}
        onClose={() => setAnchorElType(null)}
        options={searchTypeOptions}
        selectedValue={inputSearchType}
        onSelect={onChangeSearchType}
      />
      {/* Year selected button */}
      <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
      <Tooltip
        title={t("Filter.year_created")}
        arrow
        placement="top"
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 150 }}
      >
        <ButtonBase
          onClick={(e) => setAnchorElYear(e.currentTarget)}
          sx={{
            minWidth: 35,
            minHeight: 30,
            px: 0.5,
            borderRadius: "50%",
            color: theme.palette.secondary.light,
            "&:hover": {
              bgcolor: theme.palette.action.hover,
              color: theme.palette.text.secondary,
            },
          }}
        >
          {yearSelected}
        </ButtonBase>
      </Tooltip>
      {/* Menu year selected */}
      <SelectionMenu
        anchorEl={anchorElYear}
        open={Boolean(anchorElYear)}
        onClose={() => setAnchorElYear(null)}
        options={optionsYearCreated}
        selectedValue={yearSelected}
        onSelect={(year) => {
          onChangeYear(year);
          resetPagination();
        }}
        showCustomInput={true}
      />
      {/* Clean filters button */}
      {hasFilters && (
        <>
          <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
          <EGIconButton
            iconName="filter_remove"
            iconSize={20}
            title={t("Filter.remove_filters")}
            iconColor="error"
            iconColorHover="white"
            colorHover={theme.palette.error.main}
            onClick={() => {
              resetPagination();
              navigate("/reports/all/");
            }}
          />
        </>
      )}
      {/* Open filters menu */}
      <Divider sx={{ height: 20, m: 0.33 }} orientation="vertical" />
      <EGIconButton
        iconName="settings4"
        sx={{ transform: "rotate(90deg)", mx: 0.25 }}
        iconColorHover="black"
        iconSize={20}
        title={t("Filter.filter")}
        onClick={() => setShowFilters(true)}
      />
    </Paper>
  );
};
